<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import BaseBubble from './Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FormattedContent from './Text/FormattedContent.vue';
import { useMessageContext } from '../provider.js';
import MessageApi from 'dashboard/api/inbox/message';
import aiReplyApi from 'dashboard/api/sumo1/aiReply';

const { id, contentAttributes, conversationId } = useMessageContext();
const { isAWebWidgetInbox, isAnEmailChannel } = useInbox();
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const selectedChat = useMapGetter('getSelectedChat');

const isRegenerating = ref(false);
const isDismissed = ref(false);
const draftText = ref(contentAttributes.value?.aiReply ?? '');

const useRichEditor = computed(
  () => isAWebWidgetInbox.value || isAnEmailChannel.value
);

const isDraftForCurrentUser = computed(() => {
  const userId = Number(currentUser.value?.id);
  if (!userId) return false;
  const ruleOwnerId = Number(contentAttributes.value?.ruleOwnerId);
  const currentAssigneeId = Number(selectedChat.value?.meta?.assignee?.id);
  return userId === ruleOwnerId && userId === currentAssigneeId;
});

const shouldRender = computed(
  () => !isDismissed.value && isDraftForCurrentUser.value
);

const deleteDraftMessage = () => {
  MessageApi.delete(conversationId.value, id.value).catch(error => {
    useAlert(
      error?.response?.data?.message || t('AI_REPLY_DRAFT.DELETE_ERROR')
    );
  });
};

const send = () => {
  if (isRegenerating.value || !draftText.value.trim()) return;
  const text = draftText.value;
  isDismissed.value = true;
  MessageApi.create({
    conversationId: conversationId.value,
    message: text,
    private: false,
  })
    .then(deleteDraftMessage)
    .catch(error => {
      isDismissed.value = false;
      draftText.value = text;
      useAlert(
        error?.response?.data?.message || t('AI_REPLY_DRAFT.SEND_ERROR')
      );
    });
};

const edit = () => {
  if (isRegenerating.value || !draftText.value.trim()) return;
  const event = useRichEditor.value
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR;
  emitter.emit(event, draftText.value);
  isDismissed.value = true;
  deleteDraftMessage();
};

const regenerate = async () => {
  if (isRegenerating.value) return;
  const url = contentAttributes.value?.regenerateUrl;
  const event = contentAttributes.value?.regenerateEvent;
  if (!url || !event) {
    useAlert(t('AI_REPLY_DRAFT.REGENERATE_ERROR'));
    return;
  }
  isRegenerating.value = true;
  try {
    const next = await aiReplyApi.regenerate(url, {
      event,
      messageId: id.value,
      originalMessage: contentAttributes.value?.originalMessage ?? '',
      previousDraft: draftText.value,
    });
    if (next) draftText.value = next;
  } catch (error) {
    useAlert(t('AI_REPLY_DRAFT.REGENERATE_ERROR'));
  } finally {
    isRegenerating.value = false;
  }
};

const discard = () => {
  isDismissed.value = true;
  deleteDraftMessage();
};
</script>

<template>
  <BaseBubble
    v-if="shouldRender"
    class="!bg-white !text-n-slate-12 border border-n-slate-6 px-4 py-3 max-w-lg shadow-sm"
    data-bubble-name="ai-reply-draft"
  >
    <div
      class="flex items-center gap-1.5 mb-2 text-n-slate-11 text-sm font-medium"
    >
      <Icon icon="i-lucide-sparkles" class="w-4 h-4 flex-shrink-0" />
      <span>{{ t('AI_REPLY_DRAFT.TITLE') }}</span>
    </div>

    <div class="relative">
      <FormattedContent v-if="draftText" :content="draftText" />
      <div
        v-if="isRegenerating"
        class="absolute inset-0 flex items-center justify-center bg-white/70 rounded"
      >
        <span
          class="inline-block w-5 h-5 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
        />
      </div>
    </div>

    <div class="mt-3 flex flex-wrap gap-2">
      <button
        type="button"
        class="px-3 py-1.5 rounded bg-n-brand text-white text-xs font-medium transition-colors hover:enabled:brightness-110 focus-visible:brightness-110 disabled:opacity-50"
        :disabled="isRegenerating || !draftText.trim()"
        @click="send"
      >
        {{ t('AI_REPLY_DRAFT.SEND') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 rounded border border-n-slate-6 text-xs transition-colors hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 disabled:opacity-50"
        :disabled="isRegenerating || !draftText.trim()"
        @click="edit"
      >
        {{ t('AI_REPLY_DRAFT.EDIT') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 rounded border border-n-slate-6 text-xs transition-colors hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 disabled:opacity-50 inline-flex items-center gap-1.5"
        :disabled="isRegenerating"
        @click="regenerate"
      >
        <span
          v-if="isRegenerating"
          class="inline-block w-3 h-3 border-2 border-n-slate-11 border-t-transparent rounded-full animate-spin"
        />
        {{
          isRegenerating
            ? t('AI_REPLY_DRAFT.GENERATING')
            : t('AI_REPLY_DRAFT.REGENERATE')
        }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 rounded text-xs text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:bg-n-alpha-2"
        @click="discard"
      >
        {{ t('AI_REPLY_DRAFT.DISCARD') }}
      </button>
    </div>
  </BaseBubble>
</template>
